import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  MenuProps,
  PopoverOrigin,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CHANGING_COUNTRY_LABEL,
  SHIPPING_COUNTRY_SHEET_LABEL,
  SHIPPING_COUNTRY_SHEET_TITLE,
  SHIPPING_TO_CARD_MIN_HEIGHT,
  SHIPPING_TO_CARET_SIZE,
  SHIPPING_TO_LABEL,
  SHIPPING_TO_MENU_MAX_HEIGHT,
  SHIPPING_TO_MENU_ROW_HEIGHT,
  SHIPPING_TO_SHEET_MAX_HEIGHT_PERCENT,
  SHIPPING_TO_SHEET_ROW_HEIGHT,
  SHIPPING_TO_SPINNER_SIZE,
  SHIPPING_TO_VALUE_HEIGHT,
} from '../../../constants/country';
import { HOVER_MEDIA } from '../../../constants/ui';
import { changeUserCountry } from '../../../store/actions/init';
import {
  selectEffectiveCountry,
  selectEffectiveCountryName,
  selectIsCountryChanging,
  selectIsCountryGated,
  selectShippingToLabel,
} from '../../../store/selectors/init';
import { getCountryOptions } from '../../../util/country';
import { BottomSheet } from './BottomSheet';
import { CountryHint } from './CountryHint';

interface IProps {
  sx?: SxProps<Theme>;
}

const wrapperSx: SxProps<Theme> = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontSize: 16,
  lineHeight: '23px',
  color: 'text.secondary',
  flexShrink: 0,
};

const pinSx: SxProps<Theme> = { fontSize: 18 };

const labelSx: SxProps<Theme> = { fontSize: 'inherit', lineHeight: 'inherit' };

const getValueSx = (isUnset: boolean, isOpen: boolean): SxProps<Theme> => ({
  position: 'relative',
  height: SHIPPING_TO_VALUE_HEIGHT,
  minWidth: 0,
  py: 0,
  pl: 1,
  pr: `${SHIPPING_TO_CARET_SIZE}px`,
  borderRadius: 1,
  fontSize: 16,
  lineHeight: '23px',
  fontWeight: isUnset ? 'medium' : 'regular',
  color: isUnset ? 'primary.main' : 'text.primary',
  backgroundColor: isOpen ? ({ palette }) => alpha(palette.primary.main, 0.12) : 'transparent',
  [HOVER_MEDIA]: {
    '&:hover': { backgroundColor: ({ palette }) => alpha(palette.primary.main, 0.08) },
  },
  '&[aria-disabled="true"]': {
    cursor: 'default',
    [HOVER_MEDIA]: { '&:hover': { backgroundColor: 'transparent' } },
  },
});

const getCaretSx = (isUnset: boolean, isOpen: boolean): SxProps<Theme> => ({
  position: 'absolute',
  top: 4,
  right: 0,
  fontSize: SHIPPING_TO_CARET_SIZE,
  color: isUnset ? 'primary.main' : 'grey.500',
  transform: isOpen ? 'rotate(180deg)' : 'none',
  transition: ({ transitions }) => transitions.create('transform', { duration: transitions.duration.shorter }),
});

const menuPaperSx: SxProps<Theme> = { maxHeight: SHIPPING_TO_MENU_MAX_HEIGHT };

const menuAnchorOrigin: PopoverOrigin = { vertical: 'bottom', horizontal: 'right' };

const menuTransformOrigin: PopoverOrigin = { vertical: 'top', horizontal: 'right' };

const menuListProps: MenuProps['MenuListProps'] = { role: 'listbox', 'aria-label': SHIPPING_TO_LABEL };

const menuPaperProps: MenuProps['PaperProps'] = { sx: menuPaperSx };

const menuItemSx: SxProps<Theme> = { minHeight: SHIPPING_TO_MENU_ROW_HEIGHT, gap: 2 };

const checkSx: SxProps<Theme> = { ml: 'auto', fontSize: 20 };

const cardSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
  mb: 1,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  backgroundColor: 'background.paper',
  overflow: 'hidden',
};

const cardButtonSx: SxProps<Theme> = {
  width: '100%',
  minHeight: SHIPPING_TO_CARD_MIN_HEIGHT,
  pt: 1.5,
  pb: 1.5,
  pl: 1.5,
  pr: 1,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  textAlign: 'left',
  fontSize: 16,
  lineHeight: '24px',
  color: 'text.primary',
  '&[aria-disabled="true"]': { cursor: 'default' },
};

const cardPinSx: SxProps<Theme> = { fontSize: 22, color: 'action.active', flexShrink: 0 };

const cardLabelSx: SxProps<Theme> = { color: 'text.secondary', flexShrink: 0 };

const getCardValueSx = (isUnset: boolean): SxProps<Theme> => ({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: 'medium',
  color: isUnset ? 'primary.main' : 'text.primary',
});

const getCardCaretSx = (isUnset: boolean): SxProps<Theme> => ({
  fontSize: SHIPPING_TO_CARET_SIZE,
  flexShrink: 0,
  color: isUnset ? 'primary.main' : 'action.active',
});

const sheetSx: SxProps<Theme> = {
  '& .MuiDrawer-paper': { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
};

const sheetBodySx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  mx: -2,
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const sheetTitleSx: SxProps<Theme> = { px: 2, fontSize: 20, lineHeight: 1.4, flexShrink: 0 };

const sheetListSx: SxProps<Theme> = { mt: 2, p: 0 };

const sheetOptionSx: SxProps<Theme> = {
  minHeight: SHIPPING_TO_SHEET_ROW_HEIGHT,
  py: 1.5,
  px: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
  fontSize: 16,
  lineHeight: '24px',
  color: 'text.primary',
};

export const ShippingToSelector: React.FC<IProps> = ({ sx }) => {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const isGated = useSelector(selectIsCountryGated);
  const label = useSelector(selectShippingToLabel);
  const countryName = useSelector(selectEffectiveCountryName);
  const effectiveCountry = useSelector(selectEffectiveCountry);
  const isCountryChanging = useSelector(selectIsCountryChanging);

  const labelId = useId();
  const buttonId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const isMenuOpen = !!anchorEl;
  const isUnset = !countryName;
  const options = useMemo(getCountryOptions, []);

  const openPicker = useCallback(() => {
    if (isCountryChanging) {
      return;
    }

    if (isMobileViewport) {
      setIsSheetOpen(true);
    } else if (buttonRef.current) {
      setAnchorEl(buttonRef.current);
    }
  }, [isCountryChanging, isMobileViewport]);

  const closePicker = useCallback(() => {
    setIsSheetOpen(false);
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (code: string) => {
      closePicker();

      if (!isCountryChanging) {
        dispatch(changeUserCountry(code));
      }
    },
    [closePicker, dispatch, isCountryChanging],
  );

  const handleOptionClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const { code } = event.currentTarget.dataset;

      if (code) {
        handleSelect(code);
      }
    },
    [handleSelect],
  );

  const combinedSx = useMemo<SxProps<Theme>>(
    () => [isMobileViewport ? cardSx : wrapperSx, ...(Array.isArray(sx) ? sx : [sx ?? false])],
    [isMobileViewport, sx],
  );
  const valueSx = useMemo(() => getValueSx(isUnset, isMenuOpen), [isUnset, isMenuOpen]);
  const caretSx = useMemo(() => getCaretSx(isUnset, isMenuOpen), [isUnset, isMenuOpen]);
  const cardValueSx = useMemo(() => getCardValueSx(isUnset), [isUnset]);
  const cardCaretSx = useMemo(() => getCardCaretSx(isUnset), [isUnset]);

  const value = isCountryChanging ? (
    <CircularProgress size={SHIPPING_TO_SPINNER_SIZE} color="inherit" aria-label={CHANGING_COUNTRY_LABEL} />
  ) : (
    label
  );

  if (!isGated) {
    return null;
  }

  if (isMobileViewport) {
    return (
      <Box ref={wrapperRef} sx={combinedSx} data-testid="shipping-to">
        <ButtonBase
          ref={buttonRef}
          aria-haspopup="dialog"
          aria-expanded={isSheetOpen}
          aria-disabled={isCountryChanging}
          onClick={openPicker}
          sx={cardButtonSx}
        >
          <LocationOnOutlinedIcon aria-hidden sx={cardPinSx} />
          <Box component="span" sx={cardLabelSx}>
            {SHIPPING_TO_LABEL}
          </Box>
          <Box component="span" sx={cardValueSx}>
            {value}
          </Box>
          <ArrowDropDownIcon aria-hidden sx={cardCaretSx} />
        </ButtonBase>
        <CountryHint wrapperRef={wrapperRef} anchorRef={buttonRef} onChange={openPicker} isInline />
        <BottomSheet
          open={isSheetOpen}
          onClose={closePicker}
          onOpen={openPicker}
          maxHeightPercent={SHIPPING_TO_SHEET_MAX_HEIGHT_PERCENT}
          sx={sheetSx}
        >
          <Box sx={sheetBodySx}>
            <Typography component="span" fontWeight="medium" sx={sheetTitleSx}>
              {SHIPPING_COUNTRY_SHEET_TITLE}
            </Typography>
            <List role="listbox" aria-label={SHIPPING_COUNTRY_SHEET_LABEL} sx={sheetListSx}>
              {options.map(({ value: code, label: name }) => {
                const isSelected = code === effectiveCountry;

                return (
                  <ListItemButton
                    key={code}
                    role="option"
                    aria-selected={isSelected}
                    selected={isSelected}
                    data-code={code}
                    onClick={handleOptionClick}
                    sx={sheetOptionSx}
                  >
                    {name}
                    {isSelected && <CheckIcon color="primary" />}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </BottomSheet>
      </Box>
    );
  }

  return (
    <Box ref={wrapperRef} sx={combinedSx} data-testid="shipping-to">
      <LocationOnOutlinedIcon aria-hidden sx={pinSx} />
      <Typography id={labelId} component="span" sx={labelSx}>
        {SHIPPING_TO_LABEL}
      </Typography>
      <Button
        ref={buttonRef}
        id={buttonId}
        variant="text"
        aria-haspopup="listbox"
        aria-expanded={isMenuOpen}
        aria-disabled={isCountryChanging}
        aria-labelledby={`${labelId} ${buttonId}`}
        onClick={openPicker}
        sx={valueSx}
      >
        {value}
        <ArrowDropDownIcon aria-hidden sx={caretSx} />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closePicker}
        anchorOrigin={menuAnchorOrigin}
        transformOrigin={menuTransformOrigin}
        MenuListProps={menuListProps}
        PaperProps={menuPaperProps}
      >
        {options.map(({ value: code, label: name }) => {
          const isSelected = code === effectiveCountry;

          return (
            <MenuItem
              key={code}
              role="option"
              aria-selected={isSelected}
              selected={isSelected}
              data-code={code}
              onClick={handleOptionClick}
              sx={menuItemSx}
            >
              {name}
              {isSelected && <CheckIcon color="primary" sx={checkSx} />}
            </MenuItem>
          );
        })}
      </Menu>
      <CountryHint wrapperRef={wrapperRef} anchorRef={buttonRef} onChange={openPicker} />
    </Box>
  );
};
